import React, { useState, useEffect, useMemo } from "react";
import "../styles/BuyerCart.css";
import { useQuery, useMutation, gql } from "@apollo/client";

const PAGE_SIZE = 10;

const GET_BUYERS = gql`
  query GetBuyers {
    buyers {
      buyerId
      name
      email
      phoneNumber
      createdAt
    }
  }
`;

const DELETE_BUYER = gql`
  mutation DeleteBuyer($buyerId: String!) {
    deleteBuyer(buyerId: $buyerId)
  }
`;

function BuyersList() {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [page, setPage] = useState(1);
  const [filteredBuyers, setFilteredBuyers] = useState([]);

  const { loading, error: queryError, data } = useQuery(GET_BUYERS, {
    fetchPolicy: "network-only",
  });

  const [deleteBuyer, { loading: deleting }] = useMutation(DELETE_BUYER, {
    refetchQueries: [{ query: GET_BUYERS }],
    onCompleted: (data) => {
      alert(data.deleteBuyer || "Buyer deleted successfully.");
    },
    onError: (err) => {
      alert("Error deleting buyer: " + err.message);
    }
  });

  const handleDeleteBuyer = (buyerId) => {
    if (window.confirm(`Are you sure you want to completely delete buyer "${buyerId}"? This will delete all of their data across Firebase, Firestore, and MongoDB, and cannot be undone.`)) {
      deleteBuyer({ variables: { buyerId } });
    }
  };

  const buyers = useMemo(() => data?.buyers || [], [data]);
  const error = queryError ? queryError.message : null;

  // Apply search and sort filters
  useEffect(() => {
    let result = [...buyers];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(buyer =>
        buyer.buyerId.toLowerCase().includes(term) ||
        (buyer.name && buyer.name.toLowerCase().includes(term)) ||
        (buyer.email && buyer.email.toLowerCase().includes(term))
      );
    }

    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        let aVal = a[sortConfig.key] || '';
        let bVal = b[sortConfig.key] || '';

        if (sortConfig.key === 'createdAt') {
          aVal = new Date(a.createdAt);
          bVal = new Date(b.createdAt);
        }

        if (aVal < bVal) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredBuyers(result);
  }, [buyers, searchTerm, sortConfig]);
   
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
    setPage(1); // Reset to first page when sorting
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return "↕";
    return sortConfig.direction === 'ascending' ? "↑" : "↓";
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredBuyers.length / PAGE_SIZE);
  const startIndex = (page - 1) * PAGE_SIZE;
  const endIndex = Math.min(startIndex + PAGE_SIZE, filteredBuyers.length);
  const paginatedBuyers = filteredBuyers.slice(startIndex, endIndex);

  const hasNextPage = filteredBuyers.length > page * PAGE_SIZE;
  const hasPreviousPage = page > 1;

  // Format date functions
  const parseDate = (value) => {
    if (!value) return null;
    if (!isNaN(value)) {
      if (value.toString().length === 10) {
        return new Date(value * 1000);
      }
      return new Date(Number(value));
    }
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  };

  const formatFullDate = (dateValue) => {
    const date = parseDate(dateValue);
    if (!date) return "—";
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const formatShortDate = (dateValue) => {
    const date = parseDate(dateValue);
    if (!date) return "—";
    return date.toISOString().slice(0, 10);
  };

  // Export to CSV
  const exportToCSV = () => {
    if (filteredBuyers.length === 0) {
      alert("No buyers to export!");
      return;
    }

    const headers = ['Buyer ID', 'Name', 'Email', 'Phone', 'Registered Date'];
    const csvRows = filteredBuyers.map(buyer => [
      `"${buyer.buyerId || ''}"`,
      `"${buyer.name || '—'}"`,
      `"${buyer.email || '—'}"`,
      `"${buyer.phoneNumber || '—'}"`,
      `"${formatFullDate(buyer.createdAt)}"`
    ]);

    const csvContent = [
      headers.join(','),
      ...csvRows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `buyers_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export to JSON
  const exportToJSON = () => {
    if (filteredBuyers.length === 0) {
      alert("No buyers to export!");
      return;
    }

    const exportData = filteredBuyers.map(buyer => ({
      buyerId: buyer.buyerId,
      name: buyer.name || "—",
      email: buyer.email || "—",
      phoneNumber: buyer.phoneNumber   || "—",
      registeredDate: formatFullDate(buyer.createdAt),
      timestamp: buyer.createdAt
    }));

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const link = document.createElement('a');
    link.setAttribute('href', dataUri);
    link.setAttribute('download', 'buyers.json');
    link.click();
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm("");
    setPage(1);
  };

  if (loading) {
    return (
      <div className="sheet-container">
        <div className="sheet-loading">
          <div className="loading-spinner"></div>
          <p>Loading buyers data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sheet-container">
        <div className="sheet-error">
          <span className="error-icon">⚠️</span>
          <p>Error: {error}</p>
          <button className="refresh-btn" onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sheet-container">
      {/* Header Section */}
      <div className="sheet-header">
        <h1>
          <span className="sheet-icon">👤</span>
          Registered Buyers Directory
        </h1>
        <div className="sheet-actions">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by ID, name, or email..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              className="sheet-search"
            />

            {searchTerm && (
              <button
                className="clear-search-btn"
                onClick={clearSearch}
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>
          <div className="action-buttons">
            <button className="refresh-btn" onClick={() => window.location.reload()}>
              ↻ Refresh
            </button>
            <div className="export-dropdown">
              <button className="export-btn">
                📥 Export
              </button>
              <div className="export-menu">
                <button onClick={exportToCSV}>Export as CSV</button>
                <button onClick={exportToJSON}>Export as JSON</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="sheet-summary">
        <div className="summary-card">
          <span className="summary-label">Total Buyers</span>
          <span className="summary-value">{buyers.length}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">Showing</span>
          <span className="summary-value">
            {filteredBuyers.length > 0 ? `${startIndex + 1}-${endIndex}` : '0'}
            <span className="summary-subtext"> of {filteredBuyers.length}</span>
          </span>
        </div>
        <div className="summary-card">
          <span className="summary-label">Search Results</span>
          <span className="summary-value">
            {searchTerm ? `${filteredBuyers.length} found` : 'All buyers'}
          </span>
        </div>
        <div className="summary-card">
          <span className="summary-label">Page</span>
          <span className="summary-value">{page} / {totalPages || 1}</span>
        </div>
      </div>

      {/* Table Section */}
      <div className="sheet-table-container">
        <div className="sheet-table-wrapper">
          <table className="sheet-table">
            <thead>
              <tr>
                <th
                  className="sortable"
                  onClick={() => requestSort('buyerId')}
                >
                  Buyer ID
                  <span className="sort-indicator">
                    {getSortIndicator('buyerId')}
                  </span>
                </th>
                <th
                  className="sortable"
                  onClick={() => requestSort('name')}
                >
                  Name
                  <span className="sort-indicator">
                    {getSortIndicator('name')}
                  </span>
                </th>
                <th
                  className="sortable"
                  onClick={() => requestSort('email')}
                >
                  Email
                  <span className="sort-indicator">
                    {getSortIndicator('email')}
                  </span>
                </th>
                <th>Phone Number</th>
                <th
                  className="sortable"
                  onClick={() => requestSort('createdAt')}
                >
                  Registered On
                  <span className="sort-indicator">
                    {getSortIndicator('createdAt')}
                  </span>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedBuyers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="no-data-cell">
                    <div className="empty-state">
                      <span className="empty-icon">👥</span>
                      <p>
                        {searchTerm
                          ? `No buyers found for "${searchTerm}"`
                          : "No buyers registered yet"}
                      </p>
                      {searchTerm && (
                        <button
                          onClick={clearSearch}
                          className="clear-search-btn secondary"
                        >
                          Clear Search
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedBuyers.map((buyer) => (
                  <tr key={buyer.buyerId}>
                    <td className="id-cell">
                      <span className="cell-value id-value">{buyer.buyerId}</span>
                    </td>
                    <td>
                      <div className="buyer-name-cell">
                        <span className="cell-value name-value">{buyer.name || "—"}</span>
                      </div>
                    </td>
                    <td>
                      {buyer.email ? (
                        <a href={`mailto:${buyer.email}`} className="email-link">
                          {buyer.email}
                        </a>
                      ) : (
                        <span className="cell-value">—</span>
                      )}
                    </td>
                    <td>
                      {buyer.phoneNumber ? (
                        <a href={`tel:${buyer.phoneNumber}`} className="phone-link">
                          {buyer.phoneNumber}
                        </a>
                      ) : (
                        <span className="cell-value">—</span>
                      )}
                    </td>
                    <td className="date-cell">
                      <div className="date-container">
                        <span className="date-short">
                          {formatShortDate(buyer.createdAt)}
                        </span>
                        <span className="date-full">
                          {formatFullDate(buyer.createdAt)}
                        </span>
                      </div>
                    </td>
                    <td>
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteBuyer(buyer.buyerId)}
                        disabled={deleting}
                      >
                        {deleting ? "Deleting..." : "Delete"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {filteredBuyers.length > PAGE_SIZE && (
        <div className="simple-pagination">
          <button
            className="pagination-btn"
            disabled={!hasPreviousPage}
            onClick={() => setPage(p => p - 1)}
          >
            Previous
          </button>

          <div className="page-info">
            <span className="page-count">
              Page {page} of {totalPages}
            </span>
            <span className="showing-info">
              Showing {startIndex + 1}-{endIndex} of {filteredBuyers.length} buyers
            </span>
          </div>

          <button
            className="pagination-btn"
            disabled={!hasNextPage}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </button>
        </div>
      )}

      {/* Pagination for smaller datasets */}
      {filteredBuyers.length > 0 && filteredBuyers.length <= PAGE_SIZE && totalPages > 1 && (
        <div className="simple-pagination">
          <button
            className="pagination-btn"
            disabled={!hasPreviousPage}
            onClick={() => setPage(p => p - 1)}
          >
            Previous
          </button>

          <div className="page-info">
            Page {page} of {totalPages}
          </div>

          <button
            className="pagination-btn"
            disabled={!hasNextPage}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default BuyersList;